import React, { useState } from 'react';

import { DangerAlert } from '@magoosh/lib/alerts';
import { DefaultFilledButton, PrimaryButton, ResponsiveButtonGroup } from '@magoosh/lib/button';
import { PurchaseAgreement } from 'types/purchase_agreement';
import { bugsnagNotify } from 'utilities/bugsnag';

import * as api from '@test_prep/app/api';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  purchaseAgreement: PurchaseAgreement;
}

const GENERIC_ERROR = "We couldn't pause your plan. Please try again, or reach out to help@magoosh.com.";

const PausePlan: React.FC<Props> = (props) => {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Installments keep collecting through a pause, so a plan with payments still to come says so
  const onInstallments = props.purchaseAgreement.paymentMethod.installments;

  const onContinue = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await api.pausePurchaseAgreement(props.purchaseAgreement);
      props.onSuccess();
    } catch (exception) {
      setError(exception.errors?._error || GENERIC_ERROR);
      bugsnagNotify(exception);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-m">
      {error && <DangerAlert>{error}</DangerAlert>}

      <div>
        This will pause your plan.{' '}
        <strong>
          As long as your plan is paused, it will not expire and you keep all the time left with
          your plan.
        </strong>{' '}
        When you&apos;re ready to start studying again, come back here to unpause your plan and you can
        pick up right where you left off.
      </div>

      {
        onInstallments && (
          <div>
            <h5><strong>Installments</strong></h5>

            <div>
              Pausing your plan will not pause your installment payments. Installment payments will
              continue as scheduled, but your plan&apos;s end date will be extended by the pause
              duration.
            </div>
          </div>
        )
      }

      <div>Please note that you can only pause a plan once per month.</div>

      <ResponsiveButtonGroup>
        <PrimaryButton onClick={onContinue} submitting={isSubmitting}>
          Pause Plan
        </PrimaryButton>

        <DefaultFilledButton disabled={isSubmitting} onClick={props.onClose}>
          Cancel
        </DefaultFilledButton>
      </ResponsiveButtonGroup>
    </div>
  );
};

export default PausePlan;
